<script setup>
import { defineProps, defineEmits, ref, computed, watch } from 'vue';
import Modal from '@/components/modals/Modal.vue';
import LegacyDatasetTable from '@/components/table/Table.vue';
import { getLegacyDatasetsLeaderboards } from '../services/legacyDatasetService';

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    sending: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close', 'datasets-selected', 'sending', 'reload-data']);

const legacyDatasets = ref([]);
const loading = ref(false);
const hasLoaded = ref(false);

const selectedDatasets = ref([]);
const currentPage = ref(1);
const searchQuery = ref('');

const loadLegacyDatasets = async () => {
    try {
        loading.value = true;
        const data = await getLegacyDatasetsLeaderboards();
        legacyDatasets.value = data;
    } catch (error) {
        console.error('Error loading legacy datasets:', error);
        legacyDatasets.value = [];
    } finally {
        loading.value = false;
    }
};

watch(() => props.show, (newShow) => {
    if (newShow && !hasLoaded.value) {
        hasLoaded.value = true;
        loadLegacyDatasets();
    }
});

const allSelected = computed(() => {
    return selectedDatasets.value.length === legacyDatasets.value.length;
});

const columns = ref([
    {
        key: 'legacyFile',
        label: 'Legacy File',
        type: 'text',
    },
    {
        key: 'name',
        label: 'Dataset Name',
        type: 'text',
    },
    {
        key: 'seller.user.username',
        label: 'Seller',
        type: 'text',
    }
]);

const filteredDatasets = computed(() => {
    if (!searchQuery.value) {
        return legacyDatasets.value;
    }

    const query = searchQuery.value.toLowerCase();
    return legacyDatasets.value.filter(dataset => {
        return (
            dataset.legacyFile?.toLowerCase().includes(query) ||
            dataset.name?.toLowerCase().includes(query) ||
            dataset.seller?.user?.username?.toLowerCase().includes(query)
        );
    });
});

const canSelectDataset = (dataset) => {
    return true;
};

const handleClose = () => {
    emit('close');
};

const handleDatasetsSelected = (datasets) => {
    selectedDatasets.value = datasets;
};

const handlePageChange = (page) => {
    currentPage.value = page;
};

const handleSearchChange = (query) => {
    searchQuery.value = query;
    currentPage.value = 1;
};

const confirmSelection = () => {
    const datasetsToSend = allSelected.value ? [] : selectedDatasets.value;
    emit('datasets-selected', datasetsToSend);
    emit('sending', true);
};

const reloadData = () => {
    loadLegacyDatasets();
};

defineExpose({
    reloadData
});
</script>

<template>
    <Modal
        :is-open="show"
        title="Legacy Datasets"
        size="7xl"
        @close="handleClose"
    >
        <div class="space-y-6">
            <div v-if="loading" class="flex justify-center py-8">
                <div class="text-white/80">Loading legacy datasets...</div>
            </div>
            <LegacyDatasetTable
                v-else
                :select="true"
                :select-all-by-default="true"
                :current-page="currentPage"
                :data="filteredDatasets"
                :columns="columns"
                :selected-items="selectedDatasets"
                :selectable="canSelectDataset"
                :total-items="filteredDatasets.length"
                :show-pagination="true"
                :key-field="'legacyFile'"
                :show-search="true"
                :search-query="searchQuery"
                empty-message="No legacy datasets found"
                @update:selected-items="handleDatasetsSelected"
                @update:current-page="handlePageChange"
                @update:search-query="handleSearchChange"
            />
            <div class="flex justify-end space-x-3 pt-4 border-t border-echoes-border-light">
                <button
                    @click="handleClose"
                    class="px-4 py-2 text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 rounded-lg transition-colors duration-200"
                >
                    Cancel
                </button>
                <button
                    @click="confirmSelection"
                    :disabled="selectedDatasets.length === 0 || sending"
                    class="px-4 py-2 text-sm font-medium text-white bg-echoes-primary hover:bg-echoes-primary/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200 flex items-center"
                >
                    <svg v-if="sending" class="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ sending ? 'Sending...' : `Confirm Selection (${selectedDatasets.length})` }}
                </button>
            </div>
        </div>
    </Modal>
</template>